const STYLE_FILTERS = ['printed', 'embroidered', 'plain'];

/**
 * @param {string} itemStyle - Handleized style string from data-swatch-style
 * @returns {string | null} Matching style filter handle, or null if none match
 */
function resolveStyleFromItemStyle(itemStyle) {
  for (const style of STYLE_FILTERS) {
    if (itemStyle.includes(style)) return style;
  }

  return null;
}

if (!customElements.get('swatch-tray')) {
  customElements.define(
    'swatch-tray',
    class extends HTMLElement {
      constructor() {
        super();

        /** @type {string | null} */
        this.activeStyle = null;

        /** @type {string | null} */
        this.activeMotif = null;

        /** @type {string | null} */
        this.preferredStyle = null;

        /** @type {string | null} */
        this.preferredMotif = null;

        /** @type {(() => void) | null} */
        this.boundUpdateIndicators = null;

        /** @type {((e: Event) => void) | null} */
        this.boundOnModalActivate = null;

        /** @type {((e: Event) => void) | null} */
        this.boundOnModalDeactivate = null;
      }

      connectedCallback() {
        this.filters = this.querySelector('[data-swatch-filters]');
        this.motifFilters = this.querySelector('[data-swatch-motif-filters]');
        this.indicator = this.querySelector('[data-swatch-indicator]');
        this.items = this.querySelectorAll('[data-swatch-item]');
        this.buttons = this.querySelectorAll('[data-swatch-style-filter]');
        this.motifButtons = this.querySelectorAll('[data-swatch-motif-filter]');
        this.status = this.querySelector('[data-swatch-status]');
        this.trigger = document.querySelector('[data-focus-trap-toggle="swatch-tray"]:not([data-focus-trap-fallback])');
        this.preferredStyle = this.getAttribute('data-swatch-active-style');
        this.preferredMotif = this.getAttribute('data-swatch-active-motif');

        this.resetToCurrentProductFilters({ animate: false, announce: false });

        this.filters?.addEventListener('click', (e) => {
          const target = e.target;
          if (!(target instanceof Element)) return;

          const button = target.closest('[data-swatch-style-filter]');
          if (!(button instanceof HTMLButtonElement)) return;

          this.toggleFilter(button);
        });

        this.motifFilters?.addEventListener('click', (e) => {
          const target = e.target;
          if (!(target instanceof Element)) return;

          const button = target.closest('[data-swatch-motif-filter]');
          if (!(button instanceof HTMLButtonElement) || button.hidden) return;

          this.toggleMotifFilter(button);
        });

        this.boundUpdateIndicators = () => {
          this.updateIndicator({ animate: false });
        };
        this.boundOnModalActivate = (e) => {
          if (!(e instanceof CustomEvent)) return;
          if (e.detail?.id !== 'swatch-tray') return;

          this.setTriggerExpanded(true);
          this.resetToCurrentProductFilters({ animate: false, announce: false });
        };
        this.boundOnModalDeactivate = (e) => {
          if (!(e instanceof CustomEvent)) return;
          if (e.detail?.id !== 'swatch-tray') return;

          this.setTriggerExpanded(false);
        };

        window.addEventListener('resize', this.boundUpdateIndicators);
        window.addEventListener('modal-trap-activate', this.boundOnModalActivate);
        window.addEventListener('modal-trap-deactivate', this.boundOnModalDeactivate);
      }

      disconnectedCallback() {
        if (this.boundUpdateIndicators) {
          window.removeEventListener('resize', this.boundUpdateIndicators);
        }

        if (this.boundOnModalActivate) {
          window.removeEventListener('modal-trap-activate', this.boundOnModalActivate);
        }

        if (this.boundOnModalDeactivate) {
          window.removeEventListener('modal-trap-deactivate', this.boundOnModalDeactivate);
        }
      }

      /**
       * @param {boolean} expanded - Whether the swatch tray dialog is open
       */
      setTriggerExpanded(expanded) {
        if (!(this.trigger instanceof HTMLButtonElement)) return;

        this.trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      }

      /**
       * Resets filters to the current PDP product's style and motif.
       * Prefers [data-current-product] over Liquid defaults so the selected
       * swatch (and checkmark) is visible when View all opens.
       * @param {{ animate?: boolean, announce?: boolean }} [options] - Filter options
       */
      resetToCurrentProductFilters({ animate = true, announce = true } = {}) {
        const currentItem = this.querySelector('[data-swatch-item][data-current-product]');
        const defaultButton =
          this.filters?.querySelector('.option-active[data-swatch-style-filter]') || this.buttons?.[0];
        const fallbackStyle =
          this.preferredStyle ||
          (defaultButton instanceof HTMLButtonElement ? defaultButton.getAttribute('data-swatch-style-filter') : null);

        if (currentItem instanceof HTMLElement) {
          const itemStyle = currentItem.getAttribute('data-swatch-style') || '';
          const itemMotif = currentItem.getAttribute('data-swatch-motif');
          const resolvedStyle = resolveStyleFromItemStyle(itemStyle);

          this.activeStyle = resolvedStyle || fallbackStyle;
          this.preferredStyle = this.activeStyle;

          // Embroidered ignores motif filters. Otherwise the current item is source of truth
          // (including null when it has no motif — do not fall back to the first motif).
          this.preferredMotif = this.activeStyle === 'embroidered' ? null : itemMotif;
        } else {
          this.activeStyle = fallbackStyle;
        }

        this.syncMotifFilters({ preferCurrentProduct: true });
        this.applyFilter({ animate, announce });
      }

      /**
       * @param {HTMLButtonElement} button - The style filter button that was clicked
       */
      toggleFilter(button) {
        const style = button.getAttribute('data-swatch-style-filter');
        if (!style || style === this.activeStyle) return;

        this.activeStyle = style;
        this.syncMotifFilters({ preferCurrentProduct: false });
        this.applyFilter();
      }

      /**
       * @param {HTMLButtonElement} button - The motif filter button that was clicked
       */
      toggleMotifFilter(button) {
        const motif = button.getAttribute('data-swatch-motif-filter');
        if (!motif || motif === this.activeMotif) return;

        this.activeMotif = motif;
        this.applyFilter();
      }

      /**
       * @param {{ animate?: boolean, announce?: boolean }} [options] - Filter options
       */
      applyFilter({ animate = true, announce = true } = {}) {
        const activeStyle = this.activeStyle;
        if (!activeStyle) return;

        this.buttons?.forEach((filterButton) => {
          const isActive = filterButton.getAttribute('data-swatch-style-filter') === activeStyle;
          filterButton.setAttribute('aria-pressed', isActive ? 'true' : 'false');
          filterButton.classList.toggle('option-active', isActive);
        });

        this.motifButtons?.forEach((motifButton) => {
          if (!(motifButton instanceof HTMLButtonElement)) return;

          const isForActiveStyle = motifButton.getAttribute('data-swatch-motif-style') === activeStyle;
          const isActive =
            isForActiveStyle && motifButton.getAttribute('data-swatch-motif-filter') === this.activeMotif;

          motifButton.setAttribute('aria-pressed', isActive ? 'true' : 'false');
          motifButton.classList.toggle('option-active', isActive);
        });

        this.updateIndicator({ animate });

        let visibleCount = 0;

        this.items?.forEach((item) => {
          if (!(item instanceof HTMLElement)) return;

          const itemStyle = item.getAttribute('data-swatch-style') || '';
          const itemMotif = item.getAttribute('data-swatch-motif') || '';
          const styleMatch = itemStyle.includes(activeStyle);
          const motifMatch = this.activeMotif === null || itemMotif === this.activeMotif;
          const isMatch = styleMatch && motifMatch;

          item.hidden = !isMatch;
          item.setAttribute('data-active', isMatch ? 'true' : 'false');

          if (isMatch) visibleCount += 1;
        });

        if (announce) {
          this.announceResults(visibleCount);
        }
      }

      /**
       * @param {number} count - Number of visible swatch results
       */
      announceResults(count) {
        if (!(this.status instanceof HTMLElement)) return;

        const templateKey = count === 1 ? 'data-results-count-one' : 'data-results-count-other';
        const template = this.getAttribute(templateKey) || '__COUNT__';

        this.status.textContent = template.replace(/__COUNT__/g, String(count));
      }

      /**
       * @param {{ preferCurrentProduct?: boolean }} [options] - Motif sync options
       */
      syncMotifFilters({ preferCurrentProduct = false } = {}) {
        if (!(this.motifFilters instanceof HTMLElement)) {
          this.activeMotif = null;
          return;
        }

        // Embroidered never shows secondary motif filters — all embroidered products are shown.
        if (this.activeStyle === 'embroidered') {
          this.motifButtons?.forEach((motifButton) => {
            if (!(motifButton instanceof HTMLButtonElement)) return;
            motifButton.hidden = true;
          });
          this.motifFilters.hidden = true;
          this.activeMotif = null;
          return;
        }

        /** @type {string | null} */
        let firstVisibleMotif = null;
        let hasVisibleMotifs = false;
        let preferredMotifIsVisible = false;

        this.motifButtons?.forEach((motifButton) => {
          if (!(motifButton instanceof HTMLButtonElement)) return;

          const isForActiveStyle = motifButton.getAttribute('data-swatch-motif-style') === this.activeStyle;
          motifButton.hidden = !isForActiveStyle;

          if (!isForActiveStyle) return;

          hasVisibleMotifs = true;

          const motif = motifButton.getAttribute('data-swatch-motif-filter');

          if (!firstVisibleMotif) {
            firstVisibleMotif = motif;
          }

          if (preferCurrentProduct && this.preferredMotif && motif === this.preferredMotif) {
            preferredMotifIsVisible = true;
          }
        });

        this.motifFilters.hidden = !hasVisibleMotifs;

        if (!hasVisibleMotifs) {
          this.activeMotif = null;
          return;
        }

        if (preferCurrentProduct) {
          if (preferredMotifIsVisible) {
            this.activeMotif = this.preferredMotif;
            return;
          }

          // Current product has no motif for this style — show all matching style
          // so the selected swatch stays visible instead of forcing the first motif.
          if (!this.preferredMotif) {
            this.activeMotif = null;
            return;
          }
        }

        this.activeMotif = firstVisibleMotif;
      }

      /**
       * @param {{ animate?: boolean }} [options] - Indicator animation options
       */
      updateIndicator({ animate = true } = {}) {
        if (!(this.filters instanceof HTMLElement)) return;

        const activeButton = this.filters.querySelector('.option-active');
        if (!(activeButton instanceof HTMLElement)) return;

        const containerRect = this.filters.getBoundingClientRect();
        const buttonRect = activeButton.getBoundingClientRect();

        if (buttonRect.width === 0) return;

        if (!animate) {
          this.filters.classList.remove('is-animated');
        }

        this.filters.style.setProperty('--swatch-tray-indicator-inline-size', `${buttonRect.width}px`);
        this.filters.style.setProperty('--swatch-tray-indicator-offset', `${buttonRect.left - containerRect.left}px`);
        this.filters.classList.add('is-ready');

        if (!animate) {
          // Commit the snapped position before re-enabling transitions.
          void this.filters.offsetWidth;
          this.filters.classList.add('is-animated');
          return;
        }

        this.filters.classList.add('is-animated');
      }
    },
  );
}
